import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type {
  DbEvalConfigRoot,
  EvalConfigDraftContent,
  EvalConfigVersionContent,
  EvalWorkflowType,
} from '@root/domain/schemas/evals/eval-config.schema';
import axios from 'axios';

/**
 * Eval config network layer — Contract Review Bot evaluation period.
 *
 * This file, plus src/mutations/evalConfigs.ts and everything under
 * src/features/eval-config/, is intentionally isolated so the whole
 * feature can be feature-flagged or deleted when the eval is over.
 */

export type EvalConfigListItem = {
  id: string;
  name: string;
  description?: string;
  workflow_type: EvalWorkflowType;
  current_version_number?: number;
  has_unpublished_draft_changes: boolean;
  archived: boolean;
  /** The one config brokers see runs of. At most one config carries this. */
  is_champion?: boolean;
  created_at: string;
  updated_at?: string;
  created_by?: { name?: string };
  last_updated_by?: { name?: string };
};

export type EvalConfigVersionSummary = {
  version_number: number;
  published_at: string;
  published_by?: { name?: string };
  change_note?: string;
};

export type EvalConfigDetail = {
  root: DbEvalConfigRoot;
  draft: EvalConfigDraftContent | null;
  versions: EvalConfigVersionSummary[];
};

export const fetchEvalConfigs = async (): Promise<EvalConfigListItem[]> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/eval_configs',
  });
  const response = await axios(networkPayload);
  return response.data;
};

/**
 * Tenant-wide review export: every finding and every piece of reviewer
 * feedback, flattened to one row each. Rides the config collection route —
 * see the handler comment in
 * `backend/api/handlers/eval-configs/eval-review-export.handlers.ts` for why.
 * Rows come back in the CSV's own column shape; the caller writes the file.
 */
export const fetchEvalReviewExport = async (options?: {
  configId?: string;
  since?: string;
}): Promise<Record<string, string>[]> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: '/eval_configs',
    params: {
      export: 'review',
      ...(options?.configId ? { config_id: options.configId } : {}),
      ...(options?.since ? { since: options.since } : {}),
    },
  });
  const response = await axios(networkPayload);
  return response.data;
};

export const fetchEvalConfig = async (id: string): Promise<EvalConfigDetail> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/eval_configs/${id}`,
  });
  const response = await axios(networkPayload);
  return response.data;
};

export const fetchEvalConfigVersion = async (id: string, versionNumber: number): Promise<EvalConfigVersionContent> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: `/eval_configs/${id}`,
    params: { version: versionNumber },
  });
  const response = await axios(networkPayload);
  return response.data;
};
